/**
 * App — RetailCLIQ ERP shell.
 *
 * P0 Refactor:
 *   - Routing logic extracted to `@/router/AppRouter`
 *   - State extracted to hooks:
 *       `useAuthSession`, `useAppPreferences`, `useScreenNavigation`
 *   - Header + dropdowns extracted to `@/components/header/*`
 *   - Factory UAMS screens moved to `@/screens/system/SystemFactoryScreens`
 *
 * App.js is now responsible only for:
 *   • Provider composition (License / GlobalStore / ERPContext / ERPToast / GlobalErrorHandler)
 *   • Auth gate (PortalAuth vs. main shell)
 *   • Layout (Header / Sidebar / AppRouter / Taskbar)
 *   • Overlays (Nexus, ModuleLauncher, SmartAssistant, SystemLog, TableView, RightRibbon)
 */
import React, { useEffect, useState } from 'react';
import '@/App.css';
import { API_BASE } from '@/lib/api';

import { LicenseProvider, LicenseBanner } from '@/context/LicenseContext';
import { GlobalStoreProvider } from '@/store/GlobalStoreProvider';
import { ERPContextProvider } from '@/context/ERPContext';
import { ERPToastProvider } from '@/components/erp/ERPToast';
import GlobalErrorHandler from '@/components/GlobalErrorHandler';
import { SecurityGate } from '@/security/SecurityIntegration';
import UpgradeRenewModal from '@/components/UpgradeRenewModal';
import { I18nProvider } from '@/lib/manchala-i18n.react.js';

import Sidebar from '@/components/Sidebar';
import Taskbar from '@/components/Taskbar';
import NexusAssistant from '@/components/NexusAssistant';
import ModuleLauncher from '@/components/ModuleLauncher';
import SmartAssistantPanel from '@/components/SmartAssistantPanel';
import SystemLogPanel from '@/components/SystemLogPanel';
import TableViewPanel from '@/components/TableViewPanel';
import RightRibbonPanel from '@/components/RightRibbonPanel';
import { DevInspector } from '@/components/help-engine/DevInspector';
import { I18nTranslationEditor } from '@/components/help-engine/I18nTranslationEditor';
import { HelpBootstrapper } from '@/components/help-engine/HelpBootstrapper';
import { HelpCoverageBadge } from '@/components/help-engine/HelpCoverageBadge';
import { LicenseSyncEffect } from '@/components/header/LicenseHeaderChip';
import AppHeader from '@/components/header/AppHeader';
import DocumentHubModal from '@/components/document-hub/DocumentHubModal';

import PortalAuth from '@/PortalAuth';
import AppRouter from '@/router/AppRouter';
import useAuthSession from '@/hooks/useAuthSession';
import useAppPreferences from '@/hooks/useAppPreferences';
import useScreenNavigation from '@/hooks/useScreenNavigation';

function AppShell() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isNexusOpen, setIsNexusOpen] = useState(false);
  const [isLauncherOpen, setIsLauncherOpen] = useState(false);
  const [isSmartAssistantOpen, setIsSmartAssistantOpen] = useState(false);
  const [isSystemLogOpen, setIsSystemLogOpen] = useState(false);
  const [isTableViewOpen, setIsTableViewOpen] = useState(false);
  const [isRightRibbonOpen, setIsRightRibbonOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isPosMaximized, setIsPosMaximized] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [currentCompany, setCurrentCompany] = useState('C001');

  const {
    screens, activeScreenId, setActiveScreenId, activeScreen,
    navigationHistory, hubNavigationState, setHubNavigationState,
    navigateTo, closeScreen, goBack, resetToDashboard,
  } = useScreenNavigation();

  const {
    isAuthenticated, globalContextData, login, logout,
  } = useAuthSession({ onLoginReset: resetToDashboard });

  const {
    theme, toggleTheme,
    fontSize, setFontSize,
    language, setLanguage,
    currency, setCurrency,
    headerTheme, handleHeaderThemeChange,
    sidebarExpanded, setSidebarExpanded,
    isFullscreen, toggleFullscreen,
  } = useAppPreferences({ globalContextData });

  const isDark = theme === 'dark';

  // Keyboard shortcuts: Ctrl+K launcher, Ctrl+J Nexus
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setIsLauncherOpen(true); }
      if ((e.metaKey || e.ctrlKey) && e.key === 'j') { e.preventDefault(); setIsNexusOpen((p) => !p); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ─── Auth gate ───
  if (!isAuthenticated) {
    return (
      <SecurityGate>
        <GlobalErrorHandler>
          <PortalAuth onSuccess={login} />
        </GlobalErrorHandler>
      </SecurityGate>
    );
  }

  return (
    <SecurityGate>
    <LicenseProvider>
    <GlobalStoreProvider globalContextData={globalContextData}>
    <ERPContextProvider initialContext={globalContextData}>
    <ERPToastProvider>
    <GlobalErrorHandler>
      <div data-testid="app-root" className={`h-screen w-screen flex flex-col overflow-hidden select-none transition-colors duration-300 ${isDark ? 'bg-[#0a0a0a] text-white' : 'bg-[#f5f6f8] text-slate-900'}`}>

        <LicenseBanner theme={theme} />
        <LicenseSyncEffect globalContextData={globalContextData} />

        {!isPosMaximized && (
          <AppHeader
            theme={theme}
            isDark={isDark}
            sidebarExpanded={sidebarExpanded}
            setSidebarExpanded={setSidebarExpanded}
            fontSize={fontSize} setFontSize={setFontSize}
            language={language} setLanguage={setLanguage}
            currency={currency} setCurrency={setCurrency}
            headerTheme={headerTheme}
            onHeaderThemeChange={handleHeaderThemeChange}
            onToggleTheme={toggleTheme}
            isFullscreen={isFullscreen} onToggleFullscreen={toggleFullscreen}
            onOpenLauncher={() => setIsLauncherOpen(true)}
            navigateTo={navigateTo}
            isNexusOpen={isNexusOpen} onToggleNexus={() => setIsNexusOpen(p => !p)}
            isRightRibbonOpen={isRightRibbonOpen} onToggleRightRibbon={() => setIsRightRibbonOpen(p => !p)}
            onLogout={logout}
            onOpenDocHub={() => setIsImportOpen(true)}
            userRole={globalContextData?.role || ''}
          />
        )}

        {/* ─── BODY: SIDEBAR + CONTENT ─── */}
        <div className="flex flex-1 overflow-hidden">
          {!isPosMaximized && (
            <Sidebar
              onModuleSelect={navigateTo}
              activeModuleId={activeScreen?.moduleId}
              activeSubItemId={activeScreen?.subItemId}
              isRailExpanded={sidebarExpanded}
              isDrawerOpen={isDrawerOpen}
              onOpenDrawer={() => setIsDrawerOpen(true)}
              onCloseDrawer={() => setIsDrawerOpen(false)}
              theme={theme}
            />
          )}
          <main className="flex-1 overflow-hidden flex flex-col">
            <div className="flex-1 overflow-hidden">
              <AppRouter
                activeScreen={activeScreen}
                theme={theme}
                navigateTo={navigateTo}
                closeScreen={closeScreen}
                hubState={hubNavigationState}
                onHubStateChange={setHubNavigationState}
                globalContextData={globalContextData}
                isPosMaximized={isPosMaximized}
                setIsPosMaximized={setIsPosMaximized}
              />
            </div>
          </main>
        </div>

        {/* ─── TASKBAR ─── */}
        <Taskbar
          screens={screens}
          activeScreenId={activeScreenId}
          onScreenSelect={setActiveScreenId}
          onScreenClose={closeScreen}
          onGoBack={goBack}
          canGoBack={navigationHistory.length > 0}
          userName={globalContextData?.username?.toUpperCase() || 'ADMIN'}
          theme={theme}
          onSmartAssistantToggle={() => setIsSmartAssistantOpen(!isSmartAssistantOpen)}
          isSmartAssistantOpen={isSmartAssistantOpen}
          onSystemLogToggle={() => setIsSystemLogOpen(!isSystemLogOpen)}
          isSystemLogOpen={isSystemLogOpen}
          onTableViewToggle={() => setIsTableViewOpen(!isTableViewOpen)}
          isTableViewOpen={isTableViewOpen}
          companyName={globalContextData?.company?.CompName || ''}
          fiscalYear="2025-2026"
          branchName={globalContextData?.currentBranch?.Name || 'Main Branch'}
        />

        {/* Overlays */}
        <NexusAssistant isOpen={isNexusOpen} onClose={() => setIsNexusOpen(false)} activeModuleId={activeScreen?.moduleId} theme={theme} />
        <ModuleLauncher isOpen={isLauncherOpen} onClose={() => setIsLauncherOpen(false)} onLaunch={(mid) => navigateTo(mid)} theme={theme} />

        {/* Smart Assistant Panel - Right Side */}
        <SmartAssistantPanel
          isOpen={isSmartAssistantOpen}
          onToggle={() => setIsSmartAssistantOpen(!isSmartAssistantOpen)}
          userId={globalContextData?.username || 'admin'}
          userName={globalContextData?.username || ''}
          userEmail={globalContextData?.userEmail || ''}
          userPhone={globalContextData?.userPhone || ''}
          clientId={globalContextData?.company?.compId || globalContextData?.clientId || ''}
          companyName={globalContextData?.company?.compName || ''}
          theme={theme}
        />
        <SystemLogPanel isOpen={isSystemLogOpen} onToggle={() => setIsSystemLogOpen(!isSystemLogOpen)} theme={theme} />
        <TableViewPanel isOpen={isTableViewOpen} onToggle={() => setIsTableViewOpen(!isTableViewOpen)} theme={theme} activeModuleId={activeScreen?.moduleId} />

        {/* Right Ribbon Panel - User profile, session info, todos, quick actions, company selector, quick access */}
        <RightRibbonPanel
          isOpen={isRightRibbonOpen}
          onToggle={() => setIsRightRibbonOpen(!isRightRibbonOpen)}
          theme={theme}
          onLogout={logout}
          onNavigate={(moduleId) => { navigateTo(moduleId); setIsRightRibbonOpen(false); }}
          onCompanyChange={(companyId) => setCurrentCompany(companyId)}
          headerTheme={headerTheme}
          onHeaderThemeChange={handleHeaderThemeChange}
        />

        {/* Document Hub — the single unified import entry point (Module Picker ->
            Document Type -> CSV/Excel or AI/PDF). Real transactional doc types
            (Journal Voucher, PO, GRN, Purchase/Sales Invoice, KYC) post through
            the staging pipeline; the 3 Financial master-data CSV/AI imports
            (Opening Balances, GL Accounts, Chart of Accounts) delegate to the
            existing GlobalImportModal engine internally. */}
        <DocumentHubModal
          isOpen={isImportOpen}
          onClose={() => setIsImportOpen(false)}
          theme={theme}
        />
      </div>

      {/* AI Dev Inspector — Ctrl+RightClick or Shift+RightClick (sys-admin only) */}
      <DevInspector userRole={globalContextData?.role || ''} />
      {/* i18n Translation Editor — Ctrl+Shift+L (super-admin only) */}
      <I18nTranslationEditor />
      {/* AI Help Bootstrapper — floating admin panel to bulk-generate tooltip library */}
      <HelpBootstrapper userRole={globalContextData?.role || ''} />
      {/* Coverage pill — admin-only, floating bottom-left */}
      <div style={{ position: 'fixed', left: 16, bottom: 16, zIndex: 9997 }}>
        <HelpCoverageBadge userRole={globalContextData?.role || ''} />
      </div>
      {/* Upgrade / Renew License modal — opened via LicenseContext.openUpgradeModal() */}
      <UpgradeRenewModal theme={theme} />
    </GlobalErrorHandler>
    </ERPToastProvider>
    </ERPContextProvider>
    </GlobalStoreProvider>
    </LicenseProvider>
    </SecurityGate>
  );
}

export default function App() {
  return (
    <I18nProvider apiBase={API_BASE} defaultLang="en-IN">
      <AppShell />
    </I18nProvider>
  );
}
